import clsx from 'clsx';
import { MouseEvent, RefObject, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  AMPLIFY_ORANGE,
  fixMissingHttps,
  getProgramColor,
} from 'shared/lib/utils';
import { Asset } from 'shared/api/retail';
import { eventService, getPage, XApiEventKeys } from 'shared/api/event-service';

import styles from './styles.module.scss';

type ProgramQuickLinkProps = {
  programsItemRef: RefObject<HTMLLIElement>;
  programObj: Asset;
  isChecked: boolean;
  onCheckboxClick: () => void;
  isCheckboxDisabled: boolean;
  onFocus?: () => void;
  isEditMode?: boolean;
};

export const ProgramsItem = ({
  programsItemRef,
  programObj,
  onFocus,
  isEditMode,
  isChecked,
  onCheckboxClick,
  isCheckboxDisabled,
}: ProgramQuickLinkProps) => {
  const [isEditModeTooltipVisible, setIsEditModeTooltipVisible] =
    useState(false);

  const { t } = useTranslation();

  const {
    name,
    metadata: {
      dashboard: { appUrl, thumbnailUrl },
    },
  } = programObj;

  const programColor = getProgramColor(name);

  const handleProductLinkClick = (e: MouseEvent) => {
    eventService.send(XApiEventKeys.programSelected, undefined, {
      result: {
        isRightClick: e.type === 'contextmenu',
        product: name,
        url: window.location.href,
        page: getPage(),
      },
    });
  };

  const handleEditModeTooltipShow = () => {
    if (!isCheckboxDisabled) return;

    setIsEditModeTooltipVisible(true);
  };

  const handleEditModeTooltipHide = () => {
    if (!isCheckboxDisabled) return;

    setIsEditModeTooltipVisible(false);
  };

  if (isEditMode) {
    return (
      <li
        ref={programsItemRef}
        className={clsx(
          styles.programsItem,
          styles.editMode,
          'amplify-checkbox',
          {
            [styles.editModeChecked]: isChecked,
            [styles.editModeDisabled]: isCheckboxDisabled,
          },
        )}
        onMouseOver={handleEditModeTooltipShow}
        onMouseLeave={handleEditModeTooltipHide}
        data-testid="programs-item"
      >
        <span
          className={styles.coloredBackground}
          style={{ backgroundColor: programColor ?? AMPLIFY_ORANGE }}
        />
        <label
          className={clsx('d-flex', 'align-items-center', styles.programLink)}
          aria-label={name.replace(/mclass/i, 'm class')}
          onFocus={onFocus}
          onClick={handleProductLinkClick}
          onContextMenu={handleProductLinkClick}
        >
          <img className={styles.thumbnail} src={thumbnailUrl} alt="" />
          <span className={styles.programName}>{name}</span>
          <input
            type="checkbox"
            checked={isChecked}
            onChange={onCheckboxClick}
            onFocus={handleEditModeTooltipShow}
            onBlur={handleEditModeTooltipHide}
            disabled={isCheckboxDisabled}
          />
          <div className={clsx('faux-input', styles.editModeFauxInput)} />
        </label>
        {isEditModeTooltipVisible && (
          <span className={styles.editModeTooltip}>
            {t('At least one product should be selected.')}
          </span>
        )}
      </li>
    );
  }

  return (
    <li
      ref={programsItemRef}
      className={styles.programsItem}
      data-testid="programs-item"
    >
      <a
        className={clsx('d-flex', 'align-items-center', styles.programLink)}
        href={fixMissingHttps(appUrl)}
        target="_self"
        rel="noreferrer noopener"
        aria-label={name.replace(/mclass/i, 'm class')}
        onFocus={onFocus}
        onClick={handleProductLinkClick}
        onContextMenu={handleProductLinkClick}
      >
        <span
          className={styles.coloredBackground}
          style={{ backgroundColor: programColor ?? AMPLIFY_ORANGE }}
        />
        <img className={styles.thumbnail} src={thumbnailUrl} alt="" />
        <span className={styles.programName}>{name}</span>
        <i
          className={clsx('icon-v2-arrow-next', styles.arrowIcon)}
          aria-hidden
        />
      </a>
    </li>
  );
};
